import React from 'react';

import styled from '@emotion/styled';

interface TierThreshold {
  Platinum: number;
  Gold: number;
  Silver: number;
  Risk: number;
}

interface PerformanceInsightTooltipProps {
  active?: boolean;
  payload?: any[];
  tierThresholds?: TierThreshold;
}

const resolveTierFromScore = (
  score: number,
  thresholds: TierThreshold,
): { name: string; color: string } => {
  const tiers = [
    { name: 'Platinum', value: thresholds.Platinum, color: '#5b91f5' },
    { name: 'Gold', value: thresholds.Gold, color: '#ffc400' },
    { name: 'Silver', value: thresholds.Silver, color: '#6b7280' },
    { name: 'At risk', value: thresholds.Risk, color: '#ff0500' },
  ].sort((a, b) => a.value - b.value);

  for (const tier of tiers) {
    if (score <= tier.value) return { name: tier.name, color: tier.color };
  }

  // score is above all thresholds — still Platinum
  return { name: 'Platinum', color: '#5b91f5' };
};

const PerformanceInsightTooltip = ({
  active,
  payload,
  tierThresholds,
}: PerformanceInsightTooltipProps) => {
  if (active && payload && payload.length) {
    const firstEntry = payload[0];
    const averageTierScore: number = firstEntry?.value ?? 0;

    const { name: tierName, color: tierColor } = tierThresholds
      ? resolveTierFromScore(averageTierScore, tierThresholds)
      : { name: 'Unknown tier', color: '#757A85' };

    return (
      <TooltipContainer>
        <TierInfo>
          <ColorDot color={tierColor} />
          <TierLabel color={tierColor}>{tierName}</TierLabel>
        </TierInfo>

        <ScoreRow>
          <ScoreLabel>Tier score</ScoreLabel>
          <ScoreValue>{averageTierScore}</ScoreValue>
        </ScoreRow>
      </TooltipContainer>
    );
  }
  return null;
};

export default PerformanceInsightTooltip;

const TooltipContainer = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0px 10px 24px rgba(17, 24, 39, 0.12);
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 120px;
  justify-content: center;
  align-items: center;
`;

const TierInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorDot = styled.span<{ color: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  flex-shrink: 0;
`;

const TierLabel = styled.span<{ color: string }>`
  font-size: 10px;
  line-height: 10px;
  color: ${(props) => props.color};
  font-weight: 400;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const ScoreLabel = styled.span`
  font-size: 12px;
  line-height: 12px;
  font-weight: 400;
  color: #111827;
`;

const ScoreValue = styled.span`
  font-size: 12px;
  line-height: 12px;
  font-weight: 400;
  color: #111827;
`;
